import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from 'antd';

/** Messages and feedback key for one copy action. */
interface CopyOptions {
  /** Shown after a successful copy; omit for a silent copy. */
  successMessage?: string;
  /** Shown when the clipboard API rejects; omit for a silent failure. */
  errorMessage?: string;
  /** Marks which item was copied (e.g. a row id); exposed as `copiedKey` until the feedback timer ends. */
  key?: string | number;
}

/** Default time the "copied" state stays on before resetting. */
const DEFAULT_FEEDBACK_MS = 2000;

/**
 * Copies text to the clipboard with optional success/error messages and a short "copied" state
 * for icon feedback (e.g. swapping a copy icon for a check mark).
 * @param feedbackMs - How long `copiedKey` stays set after a successful copy.
 */
export function useCopyToClipboard(feedbackMs: number = DEFAULT_FEEDBACK_MS) {
  const { message } = App.useApp();
  const [copiedKey, setCopiedKey] = useState<string | number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear a pending feedback timer when the component unmounts.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  /**
   * Writes `text` to the clipboard.
   * @returns true when the copy succeeded.
   */
  const copy = useCallback(
    async (text: string, { successMessage, errorMessage, key = 'default' }: CopyOptions = {}): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text);
      } catch (error) {
        if (errorMessage) message.error(errorMessage);
        console.error('Failed to copy to clipboard:', error);
        return false;
      }
      if (successMessage) message.success(successMessage);
      setCopiedKey(key);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopiedKey(null), feedbackMs);
      return true;
    },
    [message, feedbackMs]
  );

  return { copy, copiedKey };
}
